import { errorText, get, post, put } from '@/api/client';
import { useTenantWrite, useUsers } from '@/api/data';
import { usePayrollTypes } from '@/api/payroll';
import { useShiftTypes } from '@/api/shifts';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import type { Billing } from '@/pages/Abonnement';
import { KEY, type PowerOfficeStatus, usePowerOffice } from '@/pages/lonn/PowerOffice';
import {
  BASIS,
  DEPT_NEEDED,
  type Missing,
  type PayTypeSuggestion,
  type PowerOfficeMatch,
  addFromGo,
} from '@/pages/lonn/PowerOfficeMatch';
import { KEY as TX_KEY, TripletexKeySteps, type TripletexStatus, useTripletex } from '@/pages/lonn/Tripletex';
import { POWEROFFICE, type PayrollSystem, TRIPLETEX } from '@/pages/lonn/payrollSystem';
import { PocChatPanel, draftForHugin } from '@/poc-chat/Panel';
import { useDock } from '@/poc-chat/attach';
import { typeTime } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { toast } from '@/ui';
import { fmtD } from '@/ui/format';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import css from './oppsett.module.css';

const STEPS = [
  { key: 'settings', label: 'Start' },
  { key: 'company', label: 'Bedrift' },
  { key: 'employees', label: 'Ansatte' },
  { key: 'rules', label: 'Regler' },
  { key: 'hugin', label: 'Hugin' },
  { key: 'review', label: 'Klar' },
] as const;
type Step = (typeof STEPS)[number]['key'];
const SOURCES = [
  { id: 'poweroffice', name: 'PowerOffice Go', sub: 'Vi henter ansatte, lønn og lønnsarter' },
  { id: 'tripletex', name: 'Tripletex', sub: 'Vi henter ansatte og lønnsarter, eller lager Tripletex-filen' },
  { id: 'visma', name: 'Visma', sub: 'Timer til lønn lager Visma-filen' },
] as const;
type Source = (typeof SOURCES)[number]['id'];
// what Hugin is asked from the setup: typed into its box, sent only when the person presses Send
const ASKS = [
  { label: 'Åpningstider', text: 'Sett åpningstidene våre: ' },
  { label: 'Vakttyper', text: 'Lag vakttyper for oss. Vi har disse vaktene: ' },
  { label: 'Bemanning', text: 'Sett bemanningsbehovet per ukedag: ' },
  { label: 'Første vaktplan', text: 'Lag et forslag til vaktplan for neste uke.' },
];
const osloDay = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo' }).format(new Date(iso));
const kr = (n: number) => `${n.toLocaleString('nb-NO')} kr`;
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
const nth = (i: number) => ({ '--i': i }) as CSSProperties;
// the site's booking form: help with the setup is offered, never forced (plan 0028)
const HELP = 'https://timefly.no/#demo';

/**
 * The first-run setup of a new company (plan 0028 slice 3), rebuilt as a wizard in timefly.no's look
 * (owner 2026-10-19, design canvas «Timefly onboarding wizard»): where the pay comes from, the company
 * by its org number, the people and the pay types from PowerOffice Go when the company uses it, Hugin
 * docked for what is left (opening hours, shift types, staffing), then the trial. Outside the app's
 * shell. The step is kept on the server, so a person who leaves comes back to where they were; every
 * step can be skipped, and «Hopp over oppsettet» ends it. Nothing is written before a button says what
 * it writes, nothing is written to PowerOffice, and nobody gets mail: the welcome is the
 * administrator's choice later. The panel beside the card shows what the company holds so far.
 */
export default function Oppsett() {
  const { tenant } = useTenant();
  const nav = useNavigate();
  const tw = useTenantWrite();
  const [busy, setBusy] = useState(false);
  // a new step starts at the top: the card is taller than the window on a laptop
  const stepNow = tenant?.setup_step;
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the step changes, reads nothing of it
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stepNow]);
  if (!tenant) return null;
  const step = tenant.setup_step as Step | null | undefined;
  // the setup is the administrator's; anyone else, or a company past it, goes home
  if (!step || !tenant.me?.is_admin) return <Navigate to="/" replace />;
  const at = Math.max(
    0,
    STEPS.findIndex((s) => s.key === step),
  );

  async function go(body: Parameters<typeof tw.mutateAsync>[0]['body'], done?: boolean) {
    if (!tenant) return false;
    setBusy(true);
    try {
      await tw.mutateAsync({ id: tenant.id, body });
      if (done) nav('/', { replace: true });
      return true;
    } catch (e) {
      toast(`${T('Kunne ikke lagre')}: ${errorText(e)}`);
      return false;
    } finally {
      setBusy(false);
    }
  }
  const next = () => go({ setup_step: STEPS[at + 1]?.key ?? 'review' });
  // the payroll system the next steps fetch from (#183 PowerOffice Go, #187 Tripletex); Tripletex is every
  // company's default, so its steps offer the link rather than assume it
  const sys =
    tenant.payroll_system === 'poweroffice'
      ? POWEROFFICE
      : tenant.payroll_system === 'tripletex'
        ? TRIPLETEX
        : null;
  const props = { go, next, busy, sys };

  return (
    <div className={css.page}>
      <header className={css.head}>
        <div className={css.brand}>
          <b>timefly</b>
          <span>{T('Oppsett')}</span>
        </div>
        <ol className={css.steps} aria-label={T('Steg')}>
          {STEPS.map((s, i) => (
            <li key={s.key}>
              {/* any step, in any order, before finishing (owner, 2026-09-27) */}
              <button
                type="button"
                className={i < at ? css.done : undefined}
                aria-current={i === at ? 'step' : undefined}
                disabled={busy || i === at}
                onClick={() => go({ setup_step: s.key })}
              >
                <span className={css.num} aria-hidden="true">
                  {i < at ? '✓' : i + 1}
                </span>
                {T(s.label)}
              </button>
            </li>
          ))}
        </ol>
        <button
          type="button"
          className={css.skip}
          disabled={busy}
          onClick={() => go({ setup_step: null }, true)}
        >
          {T('Hopp over oppsettet')}
        </button>
      </header>
      <main className={css.main}>
        <section className={css.card} aria-labelledby="oppsett-title">
          {step === 'settings' && <Start {...props} />}
          {step === 'company' && <Company {...props} />}
          {step === 'employees' && <People {...props} />}
          {step === 'rules' && <Rules {...props} />}
          {step === 'hugin' && <Hugin {...props} />}
          {step === 'review' && <Review {...props} />}
        </section>
        <Built />
      </main>
    </div>
  );
}

interface StepProps {
  go: (
    body: Parameters<ReturnType<typeof useTenantWrite>['mutateAsync']>[0]['body'],
    done?: boolean,
  ) => Promise<boolean>;
  next: () => Promise<boolean>;
  busy: boolean;
  sys: PayrollSystem | null;
}

/** The link to the payroll system, whichever it is: whether it can be made, whether it is, and to what. */
function useLink(sys: PayrollSystem | null) {
  const p = usePowerOffice(sys?.key === 'poweroffice');
  const t = useTripletex(sys?.key === 'tripletex');
  const d = sys?.key === 'poweroffice' ? p.data : sys?.key === 'tripletex' ? t.data : undefined;
  if (!d) return undefined;
  return {
    available: d.available,
    connected: d.connected,
    name: 'client_name' in d ? d.client_name : (d as TripletexStatus).company_name,
  };
}
function Eyebrow({ n }: { n: number }) {
  return <span className={css.eyebrow}>{T('Steg {n} av {of}', { n, of: 4 })}</span>;
}
function Foot({
  label,
  onPri,
  skip,
  busy,
  disabled,
}: { label: string; onPri: () => unknown; skip?: () => unknown; busy: boolean; disabled?: boolean }) {
  return (
    <div className={css.foot}>
      <button type="button" className={css.pri} disabled={busy || disabled} onClick={onPri}>
        {label}
      </button>
      {skip && (
        <button type="button" className={css.ghost} disabled={busy} onClick={skip}>
          {T('Hopp over dette steget')}
        </button>
      )}
    </div>
  );
}

/** Where the pay goes: the company's payroll system, which also decides what the next steps fetch. */
function Start({ go, busy }: StepProps) {
  const { tenant } = useTenant();
  const [src, setSrc] = useState<Source | 'none'>(
    tenant?.payroll_system && tenant.payroll_system !== 'tripletex' ? tenant.payroll_system : 'none',
  );
  const first = useAuth((a) => a.user?.name)?.split(' ')[0];
  return (
    <>
      <span className={css.eyebrow}>{first ? T('Velkommen, {name}', { name: first }) : T('Velkommen')}</span>
      <h1 id="oppsett-title">
        {T('Vi bygger Timefly')}
        <br />
        <span>{T('fra det dere allerede har.')}</span>
      </h1>
      <p className={css.lead}>
        {T(
          'Gi oss organisasjonsnummeret, så henter vi bedriften. Bruker dere PowerOffice Go eller Tripletex, henter vi også ansatte og lønnsartene. Du ser alt før noe lagres.',
        )}
      </p>
      <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'grid', gap: 10 }}>
        <legend style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
          {T('Hvilket lønnssystem bruker dere?')}
        </legend>
        <div className={css.sources}>
          {SOURCES.map((x) => (
            <button
              key={x.id}
              type="button"
              className={css.source}
              aria-pressed={src === x.id}
              onClick={() => setSrc(x.id)}
            >
              <strong>{x.name}</strong>
              <span>{T(x.sub)}</span>
            </button>
          ))}
          <button
            type="button"
            className={css.source}
            aria-pressed={src === 'none'}
            onClick={() => setSrc('none')}
          >
            <strong>{T('Annet, eller vet ikke')}</strong>
            <span>{T('Legg inn ansatte selv eller med Hugin')}</span>
          </button>
        </div>
      </fieldset>
      <div className={css.grow} />
      <div className={css.foot}>
        <button
          type="button"
          className={css.pri}
          disabled={busy}
          onClick={() => go({ ...(src !== 'none' ? { payroll_system: src } : {}), setup_step: 'company' })}
        >
          {T('Start oppsettet')}
        </button>
        <span className={css.muted}>{T('Tar noen minutter. Du kan hoppe over hvert steg.')}</span>
      </div>
    </>
  );
}

/** The company by its org number (the Enhetsregisteret, read on save), then PowerOffice Go's link. */
function Company({ go, next, busy, sys }: StepProps) {
  const { tenant } = useTenant();
  const tw = useTenantWrite();
  const [org, setOrg] = useState(tenant?.org_number ?? '');
  const [err, setErr] = useState<string | null>(null);
  const digits = org.replace(/\s/g, '');
  const status = usePowerOffice(sys?.key === 'poweroffice');
  const link = useLink(sys);
  const connected = !!link?.connected;
  async function find() {
    if (!tenant) return;
    setErr(null);
    try {
      await tw.mutateAsync({ id: tenant.id, body: { org_number: digits } });
    } catch (e) {
      setErr(errorText(e));
    }
  }
  return (
    <>
      <Eyebrow n={1} />
      <h2 id="oppsett-title">{T('Hvilken bedrift?')}</h2>
      <form
        className={css.field}
        onSubmit={(e) => {
          e.preventDefault();
          find();
        }}
      >
        <label htmlFor="oppsett-org">{T('Organisasjonsnummer')}</label>
        <div className={css.row}>
          <input
            id="oppsett-org"
            className={css.input}
            inputMode="numeric"
            autoComplete="off"
            value={org}
            aria-invalid={err ? true : undefined}
            aria-describedby="oppsett-org-hint"
            onChange={(e) => setOrg(e.target.value)}
          />
          <button type="submit" className={css.dark} disabled={tw.isPending || !/^\d{9}$/.test(digits)}>
            {tw.isPending ? T('Søker…') : T('Finn')}
          </button>
        </div>
        <span id="oppsett-org-hint" className={err ? css.error : css.muted}>
          {err ?? T('9 siffer. Vi slår opp i Enhetsregisteret.')}
        </span>
      </form>
      {tenant?.org_number && (
        <div className={`${css.found} ${css.pop}`} data-testid="setup-found">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <strong>{tenant.brreg_name ?? tenant.name}</strong>
            {tenant.brreg_name ? (
              <span className={`${css.chip} ${css.ok}`}>{T('Funnet i Enhetsregisteret')}</span>
            ) : (
              <span className={css.chip}>{T('Enhetsregisteret svarte ikke, nummeret er lagret')}</span>
            )}
          </div>
          <div className={css.facts}>
            <div>
              <small>{T('Organisasjonsnummer')}</small>
              {tenant.org_number}
            </div>
            {tenant.nace_text && (
              <div>
                <small>{T('Bransje')}</small>
                {tenant.nace_text}
              </div>
            )}
          </div>
        </div>
      )}
      {sys?.key === 'poweroffice' && <PowerOfficeBox status={status.data} />}
      {sys?.key === 'tripletex' && link?.available && <TripletexBox name={connected ? link.name : null} />}
      <div className={css.grow} />
      <Foot
        busy={busy}
        label={sys && connected ? T('Hent ansatte fra {system}', { system: sys.short }) : T('Neste')}
        onPri={next}
        skip={() => go({ setup_step: 'employees' })}
      />
    </>
  );
}

/** Go's approval page when the server offers it, else the client key; the same calls as Innstillinger, Lønn. */
function PowerOfficeBox({ status }: { status: PowerOfficeStatus | undefined }) {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  const [key, setKey] = useState('');
  const connect = useMutation({
    mutationFn: () => put<PowerOfficeStatus>('/admin/poweroffice', { key }),
    onSuccess: (s) => {
      qc.setQueryData(KEY(tenant?.id), s);
      setKey('');
    },
    onError: (e) => toast(errorText(e)),
  });
  // Go comes back to Innstillinger, Lønn, which sends a company still in setup back here
  const start = useMutation({
    mutationFn: () => post<{ url: string }>('/admin/poweroffice/onboarding', {}),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast(errorText(e)),
  });
  if (!status) return null;
  if (!status.available)
    return (
      <div className={css.connect}>
        <strong>{T('PowerOffice Go')}</strong>
        <span>
          {T(
            'Den direkte koblingen er ikke slått på ennå. Legg inn ansatte selv nå, og bruk PowerOffice-filen under Timer til lønn.',
          )}
        </span>
      </div>
    );
  if (status.connected)
    return (
      <div className={`${css.connect} ${css.pop}`} data-testid="setup-po-connected">
        <strong>
          {T('Koblet til PowerOffice Go')}
          {status.client_name ? `: ${status.client_name}` : ''}
        </strong>
        <span>
          {T(
            'Timefly leser ansatte og lønnsarter derfra, og skriver ingenting før du sender timer til lønn.',
          )}
        </span>
      </div>
    );
  return status.onboarding ? (
    <div className={css.connect}>
      <strong>{T('Koble til PowerOffice Go')}</strong>
      <span>
        {T('Du logger inn i PowerOffice Go og godkjenner Timefly for bedriften. Så kommer du tilbake hit.')}
      </span>
      <span>
        <button type="button" className={css.dark} disabled={start.isPending} onClick={() => start.mutate()}>
          {T('Koble til PowerOffice Go')}
        </button>
      </span>
    </div>
  ) : (
    <form
      className={css.connect}
      onSubmit={(e) => {
        e.preventDefault();
        connect.mutate();
      }}
    >
      <strong>{T('Koble til PowerOffice Go')}</strong>
      <ol>
        <li>
          {T(
            'I PowerOffice Go: åpne Meny, Innstillinger, Utvidelser. Velg Egendefinert og lim inn applikasjonsnøkkelen du har fått fra Timefly.',
          )}
        </li>
        <li>{T('Kopier klientnøkkelen Go viser før du trykker OK. Go viser den bare én gang.')}</li>
      </ol>
      <div className={css.row}>
        <input
          className={css.input}
          style={{ fontSize: 15 }}
          type="password"
          autoComplete="off"
          aria-label={T('Klientnøkkel fra PowerOffice')}
          placeholder={T('Klientnøkkel fra PowerOffice')}
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />
        <button type="submit" className={css.dark} disabled={connect.isPending || key.trim().length < 20}>
          {connect.isPending ? T('Kobler til…') : T('Koble til')}
        </button>
      </div>
    </form>
  );
}

/** Tripletex's employee key, pasted as on Innstillinger, Lønn (#187); optional, since Tripletex is the default. */
function TripletexBox({ name }: { name: string | null }) {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  const [token, setToken] = useState('');
  const connect = useMutation({
    mutationFn: () => put<TripletexStatus>('/admin/tripletex', { token }),
    onSuccess: (s) => {
      qc.setQueryData(TX_KEY(tenant?.id), s);
      setToken('');
    },
    onError: (e) => toast(errorText(e)),
  });
  if (name !== null)
    return (
      <div className={`${css.connect} ${css.pop}`} data-testid="setup-tx-connected">
        <strong>
          {T('Koblet til Tripletex')}
          {name ? `: ${name}` : ''}
        </strong>
        <span>
          {T(
            'Timefly leser ansatte og lønnsarter derfra, og skriver ingenting før du sender timer til lønn.',
          )}
        </span>
      </div>
    );
  return (
    <form
      className={css.connect}
      onSubmit={(e) => {
        e.preventDefault();
        connect.mutate();
      }}
    >
      <strong>{T('Bruker dere Tripletex? Koble til, så henter vi ansatte og lønnsarter')}</strong>
      <TripletexKeySteps />
      <div className={css.row}>
        <input
          className={css.input}
          style={{ fontSize: 15 }}
          type="password"
          autoComplete="off"
          aria-label={T('Nøkkel fra Tripletex')}
          placeholder={T('Nøkkel fra Tripletex')}
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
        <button type="submit" className={css.dark} disabled={connect.isPending || token.trim().length < 20}>
          {connect.isPending ? T('Kobler til…') : T('Koble til')}
        </button>
      </div>
    </form>
  );
}

const useMatch = (sys: PayrollSystem | null, on: boolean) =>
  useQuery({
    queryKey: [sys?.key, 'match'],
    queryFn: () => get<PowerOfficeMatch>(`${sys?.base}/match`),
    enabled: on && !!sys,
    staleTime: 0,
  });

/**
 * The people: from PowerOffice Go, the ones already here linked on the server's sure suggestion and
 * the rest made as on Koble ansatte (addFromGo, no mail); without the link, added on Ansatte as before.
 */
function People({ go, next, busy, sys }: StepProps) {
  const connected = !!useLink(sys)?.connected;
  const users = useUsers();
  const nav = useNavigate();
  const qc = useQueryClient();
  const m = useMatch(sys, connected);
  const link = (m.data?.people ?? []).filter((p) => p.sure && p.suggestion && p.suggestion !== p.current);
  const linked = (m.data?.people ?? []).filter((p) => p.current);
  const missing = m.data?.missing ?? [];
  const addable = (x: Missing) => !!x.existing || !!(x.birth_date && x.name && x.lastname);
  const [off, setOff] = useState<Set<string>>(new Set());
  // number → the server's reason, for people the create refused
  const [refused, setRefused] = useState<Record<string, string>>({});
  const on = (k: string) => !off.has(k);
  const flip = (k: string) => {
    const s = new Set(off);
    if (s.has(k)) s.delete(k);
    else s.add(k);
    setOff(s);
  };
  const toAdd = missing.filter((x) => addable(x) && on(`n${x.number}`));
  const toLink = link.filter((p) => on(`u${p.user_id}`));
  const save = useMutation({
    mutationFn: async () => {
      if (toLink.length)
        await put(`${sys?.base}/match`, {
          people: toLink.map((p) => ({ user_id: p.user_id, number: p.suggestion })),
          types: [],
        });
      return toAdd.length
        ? addFromGo(toAdd, sys ?? POWEROFFICE)
        : { made: 0, no: {} as Record<string, string> };
    },
    onSuccess: async ({ no }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: [sys?.key, 'match'] });
      // a refused person keeps their row with the reason and is left out of the next press, so the
      // rest goes on (review 2026-10-19: «added first user and stopped»)
      if (Object.keys(no).length) {
        setRefused((r) => ({ ...r, ...no }));
        setOff((o) => new Set([...o, ...Object.keys(no).map((n) => `n${n}`)]));
        toast(T('{n} kunne ikke legges til. Se grunnen på raden.', { n: Object.keys(no).length }));
      } else await next();
    },
    onError: (e) => toast(errorText(e)),
  });
  const people = (users.data ?? []).filter((u) => u.active_status !== false).length;

  if (!connected)
    return (
      <>
        <Eyebrow n={2} />
        <h2 id="oppsett-title">{T('Ansatte')}</h2>
        <p className={css.lead}>
          {T('Legg til de ansatte under Ansatte. De får ingen e-post før du selv sender velkomsten.')}{' '}
          {T('Legg inn timelønn når du legger til folk, så blir lønnskostnaden i vaktplanen riktig.')}
        </p>
        {sys && (
          // the key skipped: say where it goes later, so the step is not a dead end (#191, run A)
          <p className={css.muted} data-testid="setup-connect-later">
            {T('Koble til {system} senere under Innstillinger, Lønn, så henter vi de ansatte derfra.', {
              system: sys.short,
            })}
          </p>
        )}
        <p className={css.muted} data-testid="setup-people">
          {people === 1 ? T('1 person i bedriften nå.') : T('{n} personer i bedriften nå.', { n: people })}
        </p>
        <span>
          <button type="button" className={css.dark} onClick={() => nav('/ansatte/ny?fra=oppsett')}>
            {T('Legg til en ansatt')}
          </button>
        </span>
        <div className={css.grow} />
        <Foot busy={busy} label={T('Neste')} onPri={next} />
      </>
    );
  const rows = [
    ...linked.map((p) => ({
      k: `c${p.user_id}`,
      name: p.name,
      sub: `${T('Ansattnr')} ${p.current}`,
      chip: [T('Allerede koblet'), css.ok],
      box: null,
    })),
    ...link.map((p) => ({
      k: `u${p.user_id}`,
      name: p.name,
      sub: `${T('Ansattnr')} ${p.suggestion}`,
      chip: [T('Finnes, {why}', { why: T(BASIS[p.basis ?? 'name']?.[0] ?? '').toLowerCase() }), css.ok],
      box: true,
    })),
    ...missing.map((x) => ({
      k: `n${x.number}`,
      name: `${x.name} ${x.lastname}`.trim(),
      sub: `${T('Ansattnr')} ${x.number}${x.email ? `, ${x.email}` : ''}`,
      chip: refused[x.number]
        ? [refused[x.number], css.warn]
        : x.existing?.account
          ? [T('Har Timefly fra før, får beskjed på e-post'), css.ok]
          : x.existing
            ? [T('Finnes i {stores}', { stores: x.existing.stores.join(', ') }), css.ok]
            : addable(x)
              ? [T('Ny'), css.new]
              : [T('Mangler fødselsdato i {system}', { system: sys?.short ?? '' }), css.warn],
      box: addable(x) && !refused[x.number],
    })),
  ];
  return (
    <>
      <Eyebrow n={2} />
      <h2 id="oppsett-title">{T('Ansatte fra {system}', { system: sys?.short ?? '' })}</h2>
      <p className={css.lead}>
        {T(
          'De som allerede finnes i Timefly kobles. Resten opprettes med ansattnummer, lønn og kontrakt fra {system}. Ingen får e-post.',
          { system: sys?.short ?? '' },
        )}
      </p>
      {m.isPending ? (
        <output className={css.muted}>{T('Henter fra {system} …', { system: sys?.short ?? '' })}</output>
      ) : m.isError ? (
        <p className={css.error}>{errorText(m.error)}</p>
      ) : rows.length === 0 ? (
        // nothing to fetch said in words, not a blank step (walk-through 2026-10-21, #191 F2)
        <div className={css.muted} data-testid="setup-people-none">
          <p>
            {T(
              'Fant ingen ansatte å hente fra {system}. Bare ansatte med ansattnummer der kommer med. Gi dem ansattnummer i {system} og hent på nytt, eller legg dem inn i Timefly senere.',
              { system: sys?.short ?? '' },
            )}
          </p>
          <button type="button" className={css.dark} disabled={m.isFetching} onClick={() => void m.refetch()}>
            {m.isFetching ? T('Henter…') : T('Hent på nytt')}
          </button>
        </div>
      ) : (
        <ul className={css.list} data-testid="setup-po-people">
          {rows.map((r, i) => (
            <li key={r.k} style={nth(i)}>
              {r.box === null ? (
                <span style={{ width: 20 }} />
              ) : (
                <input
                  type="checkbox"
                  id={`op-${r.k}`}
                  disabled={!r.box}
                  checked={!!r.box && on(r.k)}
                  onChange={() => flip(r.k)}
                />
              )}
              <span className={css.av} aria-hidden="true">
                {initials(r.name)}
              </span>
              <label htmlFor={`op-${r.k}`}>
                <b>{r.name}</b>
                <span>{r.sub}</span>
              </label>
              <span className={`${css.chip} ${r.chip[1]}`}>{r.chip[0]}</span>
            </li>
          ))}
        </ul>
      )}
      {m.data?.department_needed && <p className={css.muted}>{T(DEPT_NEEDED)}</p>}
      <div className={css.grow} />
      <Foot
        busy={busy || save.isPending}
        label={
          save.isPending
            ? T('Lagrer…')
            : toAdd.length || toLink.length
              ? T('Opprett {a} og koble {b}', { a: toAdd.length, b: toLink.length })
              : T('Neste')
        }
        onPri={() => (toAdd.length || toLink.length ? save.mutate() : next())}
        skip={() => go({ setup_step: 'rules' })}
      />
    </>
  );
}

/**
 * The pay types: Vanlige timer's code from Go's suggestion, and Go's supplements and overtime that no
 * pay type here uses yet, made as on Koble ansatte og lønnsarter. Go does not say when a supplement
 * applies, so that is set in Innstillinger, Lønn. An item over 100 % (base and supplement, «Overtid 50%»
 * at 150 %) is made as a type that pays the whole hour, at its supplement (#183, owner 2026-10-19).
 */
function Rules({ go, next, busy, sys }: StepProps) {
  const connected = !!useLink(sys)?.connected;
  const qc = useQueryClient();
  const m = useMatch(sys, connected);
  // Go's supplements and overtime with their rates; Tripletex's salary types carry none (#187), so
  // there only Vanlige timer's code is taken here and the supplements wait for Innstillinger, Lønn
  const sug = useQuery({
    queryKey: ['poweroffice-pay-types'],
    queryFn: () => get<{ suggestions: PayTypeSuggestion[] }>('/admin/poweroffice/pay-types'),
    enabled: connected && sys?.key === 'poweroffice',
  });
  const regular = m.data?.types.find((t) => t.key === 'regular');
  const regularItem = m.data?.pay_items.find((i) => i.code === regular?.suggestion);
  const list = sug.data?.suggestions ?? [];
  const [rate, setRate] = useState<Record<string, number>>({});
  // ticked by the person, else by default: Vanlige timer, and an item Go gives a rate; a kroner item
  // without one waits for its rate (typing one ticks it)
  const [pick, setPick] = useState<Record<string, boolean>>({});
  const isOn = (k: string, dflt: boolean) => pick[k] ?? dflt;
  const takeRegular =
    !!regular?.suggestion && regular.suggestion !== regular.current && isOn('regular', true);
  const chosen = list.filter((x) => isOn(x.code, x.rate_value > 0));
  const rateOf = (x: PayTypeSuggestion) => rate[x.code] ?? x.rate_value;
  // a kroner supplement at 0 kr would send its hours unpaid: it needs its rate first (owner 2026-10-19)
  const zero = chosen.filter((x) => x.calculation_type === 'extra_per_hour' && !(rateOf(x) > 0));
  const save = useMutation({
    mutationFn: async () => {
      if (takeRegular)
        await put(`${sys?.base}/match`, {
          people: [],
          types: [{ key: 'regular', code: regular?.suggestion }],
        });
      for (const x of chosen)
        await post('/admin/payroll/types', {
          name: x.name,
          code: x.code,
          calculation_type: x.calculation_type,
          rate_value: rateOf(x),
          ...(x.pays_whole_hour ? { pays_whole_hour: true } : {}),
        });
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['payroll-types'] });
      qc.invalidateQueries({ queryKey: ['poweroffice-pay-types'] });
      await next();
    },
    onError: (e) => toast(errorText(e)),
  });
  const flip = (k: string, now: boolean) => setPick({ ...pick, [k]: !now });
  if (!connected)
    return (
      <>
        <Eyebrow n={3} />
        <h2 id="oppsett-title">{T('Lønnsarter og tillegg')}</h2>
        <p className={css.lead}>
          {T(
            'Tillegg for kveld, helg og overtid, og lønnsartene fra lønnssystemet, settes under Innstillinger, Lønn. Det kan vente til første lønn.',
          )}
        </p>
        <div className={css.grow} />
        <Foot busy={busy} label={T('Neste')} onPri={next} />
      </>
    );
  const n = chosen.length + (takeRegular ? 1 : 0);
  let i = 0;
  return (
    <>
      <Eyebrow n={3} />
      <h2 id="oppsett-title">{T('Regler fra lønnsartene')}</h2>
      <p className={css.lead}>
        {T(
          'Dette er lønnsartene {system} har for timelønn, tillegg og overtid. Satsen per person kommer fra lønnen deres.',
          { system: sys?.short ?? '' },
        )}
      </p>
      {m.isPending || (sys?.key === 'poweroffice' && sug.isPending) ? (
        <output className={css.muted}>{T('Leser lønnsartene …')}</output>
      ) : (
        <div className={css.rules} data-testid="setup-po-rules">
          {regular?.suggestion && (
            <div className={css.rule} style={nth(i++)}>
              <div className={css.ruleTop}>
                <input
                  type="checkbox"
                  id="op-regular"
                  disabled={regular.suggestion === regular.current}
                  checked={regular.suggestion === regular.current || isOn('regular', true)}
                  onChange={() => flip('regular', isOn('regular', true))}
                />
                <span className={css.code}>{regular.suggestion}</span>
                <label htmlFor="op-regular">{regularItem?.name ?? T('Timelønn')}</label>
                <span className={css.muted}>{T('Vanlige timer')}</span>
              </div>
            </div>
          )}
          {list.map((x) => (
            <div key={x.code} className={css.rule} style={nth(i++)}>
              <div className={css.ruleTop}>
                <input
                  type="checkbox"
                  id={`op-pt-${x.code}`}
                  checked={isOn(x.code, x.rate_value > 0)}
                  onChange={() => flip(x.code, isOn(x.code, x.rate_value > 0))}
                />
                <span className={css.code}>{x.code}</span>
                <label htmlFor={`op-pt-${x.code}`}>{x.name}</label>
                <input
                  className={css.rate}
                  inputMode="decimal"
                  aria-label={T('Sats for {name}', { name: x.name })}
                  value={String(rateOf(x)).replace('.', ',')}
                  onChange={(e) => {
                    const v = Number(e.target.value.replace(',', '.')) || 0;
                    setRate({ ...rate, [x.code]: v });
                    if (v > 0) setPick({ ...pick, [x.code]: true });
                  }}
                />
                <span className={css.muted}>
                  {x.pays_whole_hour
                    ? T('% tillegg')
                    : x.calculation_type === 'percent'
                      ? T('% av timelønn')
                      : T('kr per time')}
                </span>
              </div>
              {x.pays_whole_hour && (
                <div className={css.muted} style={{ paddingLeft: 32, fontSize: 13 }}>
                  {T(
                    'Betaler grunnlønn og tillegg sammen ({p} % i PowerOffice). Timene flyttes fra Vanlige timer til denne linjen, så grunnlønnen betales én gang.',
                    { p: x.percent ?? '' },
                  )}
                </div>
              )}
            </div>
          ))}
          {!regular?.suggestion && !list.length && (
            <p className={css.muted}>
              {T('Alle lønnsartene for tillegg og overtid finnes allerede i Timefly.')}
            </p>
          )}
        </div>
      )}
      <p className={css.muted} style={{ margin: 0 }}>
        {T(
          'Når et tillegg gjelder (kveld, helg, natt) vet ikke {system}. Det setter du under Innstillinger, Lønn.',
          { system: sys?.short ?? '' },
        )}
      </p>
      {zero.length > 0 && (
        <p className={css.error} style={{ margin: 0 }}>
          {T('Gi {names} en sats over 0 kr, ellers sendes timene uten tillegg.', {
            names: zero.map((x) => x.name).join(', '),
          })}
        </p>
      )}
      <div className={css.grow} />
      <Foot
        busy={busy || save.isPending}
        disabled={zero.length > 0}
        label={
          save.isPending
            ? T('Lagrer…')
            : n === 1
              ? T('Opprett 1 regel')
              : n
                ? T('Opprett {n} regler', { n })
                : T('Neste')
        }
        onPri={() => (n ? save.mutate() : next())}
        skip={() => go({ setup_step: 'hugin' })}
      />
    </>
  );
}
/**
 * The rest with Hugin: the one conversation (poc-chat PocChatPanel) docked into the card, as Vaktplan's
 * Hugin tab does (only an administrator is here, and an administrator may use Hugin). A suggestion only types the words into its box; nothing is asked before Send, and
 * Hugin's changes wait for the person's yes as everywhere else.
 */
function Hugin({ next, busy }: StepProps) {
  const { tenant } = useTenant();
  useEffect(() => {
    useDock.getState().set({ line: `${T('Oppsett')}, ${tenant?.name ?? ''}` });
    return () => useDock.getState().set({ target: null });
  }, [tenant?.name]);
  return (
    <>
      <Eyebrow n={4} />
      <h2 id="oppsett-title">{T('Resten tar vi i en prat')}</h2>
      <p className={css.lead}>
        {T(
          'Fortell Hugin om åpningstider, vaktene og hvor mange dere trenger. Hugin foreslår, og ingenting lagres før du sier ja.',
        )}
      </p>
      <div className={css.asks}>
        {ASKS.map((a) => (
          <button key={a.label} type="button" onClick={() => draftForHugin(T(a.text))}>
            {T(a.label)}
          </button>
        ))}
      </div>
      <div
        className={css.chat}
        ref={(el) => useDock.getState().set({ target: el })}
        data-testid="setup-hugin"
      />
      <PocChatPanel />
      <div className={css.grow} />
      <Foot busy={busy} label={T('Neste')} onPri={next} />
    </>
  );
}

/** The trial and, with billing on, the price and the card (plan 0028 slice 4). */
function Review({ go, busy }: StepProps) {
  const { tenant } = useTenant();
  const nav = useNavigate();
  const users = useUsers();
  const pay = usePayrollTypes();
  const types = useShiftTypes();
  // the regular hours' code set from the payroll system is a pay type too (#191 F4)
  const payCount = (pay.data?.length ?? 0) + (tenant?.regular_pay_code ? 1 : 0);
  const billingOn = !!tenant?.billing?.enabled && !tenant.is_demo;
  const billing = useQuery({
    queryKey: ['billing'],
    queryFn: () => get<Billing>('/billing'),
    enabled: billingOn,
  });
  const [paying, setPaying] = useState(false);
  if (!tenant) return null;
  const people = (users.data ?? []).filter((u) => u.active_status !== false).length;
  const q = billing.data?.quote;
  // finish, then Stripe's Checkout; it comes back to Abonnement, which waits for Stripe's word
  async function finishAndPay() {
    if (!(await go({ setup_step: null }))) return;
    setPaying(true);
    try {
      window.location.assign((await post<{ url: string }>('/billing/checkout', {})).url);
    } catch (e) {
      toast(errorText(e));
      nav('/abonnement', { replace: true });
    }
  }
  return (
    <>
      <span className={css.eyebrow} style={{ color: 'var(--green)' }}>
        {T('Klart')}
      </span>
      <h2 id="oppsett-title">
        {tenant.workspace?.enabled?.scheduling === false
          ? T('{company} er klar til å registrere timer.', { company: tenant.brreg_name ?? tenant.name })
          : T('{company} er klar for første plan.', { company: tenant.brreg_name ?? tenant.name })}
      </h2>
      <div className={css.stats}>
        <div>
          <b>{people}</b>
          <span>{T('ansatte')}</span>
        </div>
        <div>
          <b>{payCount}</b>
          <span>{T('lønnsarter')}</span>
        </div>
        <div>
          <b>{types.data?.length ?? 0}</b>
          <span>{T('vakttyper')}</span>
        </div>
      </div>
      <ul className={css.summary}>
        {tenant.trial_ends_at && (
          <li>
            {T('Gratis prøveperiode til {date}. Du blir ikke belastet.', {
              date: fmtD(osloDay(tenant.trial_ends_at)),
            })}
          </li>
        )}
        {q && (
          <li data-testid="setup-price">
            {T(
              'Deretter {sum} per måned eks. mva ({base} inkluderer {included} ansatte, du har {seats}; administratorer teller ikke).',
              {
                sum: kr(q.monthly_ex_vat),
                seats: q.seats,
                base: kr(q.base_kr),
                included: q.included,
              },
            )}
          </li>
        )}
        <li>{T('De ansatte får ingen e-post før du sender velkomsten fra Ansatte.')}</li>
      </ul>
      <div className={css.grow} />
      <div className={css.foot}>
        {billingOn ? (
          <>
            <button type="button" className={css.pri} disabled={busy || paying} onClick={finishAndPay}>
              {T('Legg inn kort og start')}
            </button>
            <button
              type="button"
              className={css.ghost}
              disabled={busy || paying}
              onClick={() => go({ setup_step: null }, true)}
            >
              {T('Ferdig, legg inn kort senere')}
            </button>
          </>
        ) : (
          <button
            type="button"
            className={css.pri}
            disabled={busy}
            onClick={() => go({ setup_step: null }, true)}
          >
            {T('Ferdig')}
          </button>
        )}
      </div>
      {billingOn && (
        <p className={css.muted} style={{ margin: 0 }}>
          {T('Kortet belastes først når prøveperioden er over. Du kan si opp når som helst.')}
        </p>
      )}
      <p className={css.links}>
        <a href={HELP} target="_blank" rel="noreferrer">
          {T('Få hjelp med oppsettet')}
        </a>
      </p>
    </>
  );
}

/** What the company holds so far, read back from the server: people, pay types, shift types. */
function Built() {
  const { tenant } = useTenant();
  const users = useUsers();
  const pay = usePayrollTypes();
  const types = useShiftTypes();
  const people = (users.data ?? []).filter((u) => u.active_status !== false);
  const regular = tenant?.regular_pay_code;
  const pays = pay.data ?? [];
  const shifts = types.data ?? [];
  return (
    <aside className={css.aside} aria-label={T('Det som er satt opp')}>
      <div className={css.asideHead}>
        <strong>{T('{company} i Timefly', { company: tenant?.brreg_name ?? tenant?.name ?? '' })}</strong>
        <span>{T('Oppdateres mens du går')}</span>
      </div>
      <div className={css.box}>
        <div className={css.boxHead}>
          {T('Ansatte')} <b>{people.length}</b>
        </div>
        <div className={css.people} data-testid="setup-built-people">
          {people.slice(0, 16).map((u) => (
            <div key={u.id}>
              <span className={css.av} aria-hidden="true">
                {initials(`${u.name ?? ''} ${u.lastname ?? ''}`)}
              </span>
              {u.name}
            </div>
          ))}
        </div>
        {people.length > 16 && <p className={css.muted}>{T('og {n} til', { n: people.length - 16 })}</p>}
      </div>
      <div className={css.box}>
        <div className={css.boxHead}>
          {T('Lønnsarter')} <b>{(pay.data?.length ?? 0) + (regular ? 1 : 0)}</b>
        </div>
        {pays.length || regular ? (
          <div className={css.lines}>
            {regular && (
              <div data-testid="setup-built-regular">
                <small>{regular}</small>
                <span>{T('Vanlige timer')}</span>
                <em />
              </div>
            )}
            {pays.map((p) => (
              <div key={p.id}>
                <small>{p.code}</small>
                <span>{p.name}</span>
                <em>{p.rateType === 'percent' ? `${p.value} %` : `${p.value} kr/t`}</em>
              </div>
            ))}
          </div>
        ) : (
          <div className={css.empty}>{T('Tillegg og overtid kommer i steg 3')}</div>
        )}
      </div>
      <div className={css.box}>
        <div className={css.boxHead}>
          {T('Vakttyper')} <b>{shifts.length}</b>
        </div>
        {shifts.length ? (
          <div className={css.lines}>
            {shifts.map((s) => (
              <div key={s.id}>
                <span>{s.name}</span>
                <em>
                  {s.start_time && s.end_time ? `${typeTime(s.start_time)}–${typeTime(s.end_time)}` : ''}
                </em>
              </div>
            ))}
          </div>
        ) : (
          <div className={css.empty}>{T('Lag dem med Hugin i steg 4')}</div>
        )}
      </div>
      <p className={css.asideNote}>{T('Ingenting skrives til lønnssystemet under oppsettet.')}</p>
    </aside>
  );
}
